import React from "react";

import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import AvatarUploader from "@/components/avatar-upload";
import Button from "@/components/button";
import { AppErrorBoundary } from "@/components/ErrorBoundary";
import Input from "@/components/input";
import { PlacesAutocomplete } from "@/components/places-autocomplete";
import { OWNER_ROLES, RESTAURANT_TYPE_GROUPS } from "@/constants/ownerOnboarding";
import { isApiError } from "@/queries/api/apiUtils";
import {
  uploadOwnerPhoto,
  uploadOwnerRestaurantImage,
} from "@/queries/api/ownerOnboardingApi";
import { QueryKeys } from "@/queries/queryKeys";
import {
  OwnerOnboardingProvider,
  useOwnerOnboarding,
} from "@/routes/app/$role/setup/-owner-onboarding-context";
import { notify } from "@/utils";
import { prepareImageForUpload, validateImageFile } from "@/utils/image-upload";
import { Logger } from "@/utils/logger";

import { ChangePasswordDialog } from "./change-password-dialog";

const SELECT_CLASS =
  "rounded-md border border-garnet/40 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-garnet";

/**
 * Business Config. Edits the same owner record as the onboarding wizard
 * (GET/PUT /owner-onboarding/me) so everything set during setup — address,
 * website, owner role, photos — stays editable here.
 */
export function BusinessProfileSettings() {
  return (
    <OwnerOnboardingProvider>
      <BusinessProfileForm />
    </OwnerOnboardingProvider>
  );
}

type ImageKind = "restaurant" | "owner";

function BusinessProfileForm() {
  const { t } = useTranslation("onboarding");
  const { t: ts } = useTranslation("settings");
  const queryClient = useQueryClient();
  const {
    data,
    patch,
    setRestaurantImageUrl,
    setOwnerPhotoUrl,
    saveDraft,
    isLoading,
    isSaving,
  } = useOwnerOnboarding();
  const [uploading, setUploading] = React.useState<ImageKind | null>(null);

  const handleImage = async (kind: ImageKind, file: File) => {
    const keys =
      kind === "restaurant"
        ? {
            typeError: "ownerRestaurant.imageTypeError",
            tooLarge: "ownerRestaurant.imageTooLarge",
            tooLargeServer: "ownerRestaurant.imageTooLargeServer",
            uploaded: "ownerRestaurant.imageUploaded",
            failed: "ownerRestaurant.imageUploadFailed",
          }
        : {
            typeError: "ownerAboutYou.photoTypeError",
            tooLarge: "ownerAboutYou.photoTooLarge",
            tooLargeServer: "ownerAboutYou.photoTooLargeServer",
            uploaded: "ownerAboutYou.photoUploaded",
            failed: "ownerAboutYou.photoUploadFailed",
          };
    setUploading(kind);
    try {
      let prepared: File;
      try {
        prepared = await prepareImageForUpload(file);
      } catch {
        notify.error(t(keys.typeError));
        return;
      }
      const valid = validateImageFile(prepared);
      if (!valid.ok) {
        notify.error(t(valid.reason === "size" ? keys.tooLarge : keys.typeError));
        return;
      }
      if (kind === "restaurant") {
        setRestaurantImageUrl(await uploadOwnerRestaurantImage(prepared));
        queryClient.invalidateQueries({ queryKey: [QueryKeys.BUSINESS_PROFILE] });
      } else {
        setOwnerPhotoUrl(await uploadOwnerPhoto(prepared));
      }
      notify.success(t(keys.uploaded));
    } catch (e) {
      Logger.error(`${kind} image upload failed`, {
        error: e instanceof Error ? e.message : String(e),
        status: isApiError(e) ? e.status : undefined,
      });
      notify.error(
        t(isApiError(e) && e.status === 413 ? keys.tooLargeServer : keys.failed),
      );
    } finally {
      setUploading(null);
    }
  };

  const onSave = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!data.restaurantName.trim()) {
      notify.error(t("ownerRestaurant.nameRequired"));
      return;
    }
    if (!data.restaurantType.trim()) {
      notify.error(t("ownerRestaurant.typeRequired"));
      return;
    }
    if (!data.restaurantAddress?.formattedAddress.trim()) {
      notify.error(t("ownerRestaurant.addressRequired"));
      return;
    }
    if (data.websiteUrl.trim()) {
      try {
        new URL(data.websiteUrl.trim());
      } catch {
        notify.error(t("ownerRestaurant.websiteInvalid"));
        return;
      }
    }
    if (!data.ownerRole.trim()) {
      notify.error(t("ownerAboutYou.roleRequired"));
      return;
    }
    try {
      await saveDraft();
      await queryClient.invalidateQueries({
        queryKey: [QueryKeys.BUSINESS_PROFILE],
      });
      notify.success(ts("business.saved"));
    } catch (err) {
      Logger.error("business settings save failed", {
        error: err instanceof Error ? err.message : String(err),
      });
      notify.error(err instanceof Error ? err.message : ts("business.saveError"));
    }
  };

  if (isLoading) {
    return <div>{ts("actions.loading", { ns: "common" })}</div>;
  }

  // Keep a legacy free-text type selectable even if it isn't in the list.
  const knownTypes = RESTAURANT_TYPE_GROUPS.flatMap((g) => g.options);
  const legacyType =
    data.restaurantType && !knownTypes.includes(data.restaurantType)
      ? data.restaurantType
      : null;

  return (
    <div className="flex flex-col gap-6">
      <h3 className="text-xl font-semibold text-garnet mb-2">
        {ts("business.title")}
      </h3>
      <form onSubmit={onSave} className="flex flex-col gap-6">
        <div className="grid grid-cols-1 md:grid-cols-2 w-full gap-[15px]">
          <Input
            variantSize="sm"
            label={t("ownerRestaurant.nameLabel")}
            placeholder={t("ownerRestaurant.namePlaceholder")}
            value={data.restaurantName}
            onChange={(e) => patch({ restaurantName: e.currentTarget.value })}
          />
          <div className="flex flex-col gap-1">
            <label className="body-small text-gray-700" htmlFor="settings-restaurant-type">
              {t("ownerRestaurant.typeLabel")}
            </label>
            <select
              id="settings-restaurant-type"
              value={data.restaurantType}
              onChange={(e) => patch({ restaurantType: e.currentTarget.value })}
              className={SELECT_CLASS}
            >
              <option value="">{t("ownerRestaurant.typePlaceholder")}</option>
              {legacyType && <option value={legacyType}>{legacyType}</option>}
              {RESTAURANT_TYPE_GROUPS.map((g) => (
                <optgroup key={g.group} label={g.group}>
                  {g.options.map((opt) => (
                    <option key={opt} value={opt}>
                      {opt}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </div>
          <div className="md:col-span-2">
            <AppErrorBoundary
              fallback={
                <Input
                  variantSize="sm"
                  label={t("ownerRestaurant.addressLabel")}
                  placeholder={t("ownerRestaurant.addressPlaceholder")}
                  value={data.restaurantAddress?.formattedAddress ?? ""}
                  onChange={(e) =>
                    patch({
                      restaurantAddress: { formattedAddress: e.currentTarget.value },
                    })
                  }
                />
              }
            >
              <PlacesAutocomplete
                label={t("ownerRestaurant.addressLabel")}
                placeholder={t("ownerRestaurant.addressPlaceholder")}
                value={data.restaurantAddress?.formattedAddress ?? ""}
                onChange={(e) => {
                  const next = (e.target as HTMLInputElement).value;
                  patch({
                    restaurantAddress: {
                      ...(data.restaurantAddress ?? { formattedAddress: "" }),
                      formattedAddress: next,
                    },
                  });
                }}
                onPlaceSelect={(place) => {
                  if (!place) return;
                  const formatted = place.formattedAddress ?? place.name ?? "";
                  patch({
                    restaurantAddress: {
                      formattedAddress: formatted,
                      // Street / city / state / ZIP from Google, used for billing sales tax.
                      address1: place.components?.address1 ?? formatted,
                      city: place.components?.city,
                      state: place.components?.state,
                      zipcode: place.components?.zipcode,
                      latitude: place.location?.lat,
                      longitude: place.location?.lng,
                    },
                  });
                }}
              />
            </AppErrorBoundary>
          </div>
          <div className="md:col-span-2">
            <Input
              variantSize="sm"
              label={t("ownerRestaurant.websiteLabel")}
              placeholder={t("ownerRestaurant.websitePlaceholder")}
              value={data.websiteUrl}
              onChange={(e) => patch({ websiteUrl: e.currentTarget.value })}
            />
          </div>
          <div className="flex flex-col gap-1">
            <label className="body-small text-gray-700" htmlFor="settings-owner-role">
              {t("ownerAboutYou.yourRole")}
            </label>
            <select
              id="settings-owner-role"
              value={data.ownerRole}
              onChange={(e) => patch({ ownerRole: e.currentTarget.value })}
              className={SELECT_CLASS}
            >
              <option value="">{t("ownerAboutYou.chooseRole")}</option>
              {OWNER_ROLES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="flex flex-wrap gap-10 mt-4">
          <div className="flex flex-col gap-2 items-start">
            <span className="body-small text-gray-700">
              {t("ownerRestaurant.photoLabel")}
            </span>
            <AvatarUploader
              size="xl"
              value={data.restaurantImageUrl ?? null}
              onChange={(file) => file && void handleImage("restaurant", file)}
              loading={uploading === "restaurant"}
              maxSize={5 * 1024 * 1024}
              note={ts("business.logoMaxSize")}
            />
          </div>
          <div className="flex flex-col gap-2 items-start">
            <span className="body-small text-gray-700">
              {t("ownerAboutYou.profilePhoto")}
            </span>
            <AvatarUploader
              size="xl"
              value={data.ownerPhotoUrl ?? null}
              onChange={(file) => file && void handleImage("owner", file)}
              loading={uploading === "owner"}
              maxSize={5 * 1024 * 1024}
              note={ts("business.logoMaxSize")}
            />
          </div>
        </div>

        <div className="flex justify-end">
          <Button
            type="submit"
            variant="primary"
            disabled={isSaving || uploading !== null}
          >
            {isSaving
              ? ts("actions.saving", { ns: "common" })
              : ts("actions.saveChanges", { ns: "common" })}
          </Button>
        </div>
      </form>

      <ChangePasswordDialog />
    </div>
  );
}
